import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Checkin, CheckinSettings, CheckinWords } from '@refuelled/shared'
import { api } from '@/api/client'
import { today as todayString } from '@/lib/dates'
import { remindersAvailable, scheduleCheckinReminder } from '@/lib/reminders'

/** Do not put the reminder back more often than this. */
const ENSURE_GAP_MS = 30 * 60_000

/**
 * The daily check-in (ADR-024).
 *
 * Nothing here computes anything from what somebody said. There is no
 * average, no streak, no run of good days: ADR-024 keeps W-100's refusal of
 * any figure derived from a mood, and a store is exactly where such a thing
 * would appear by accident.
 */
export const useCheckinStore = defineStore('checkin', () => {
  const words = ref<CheckinWords>({ body: [], mind: [] })
  const today = ref<Checkin | null>(null)
  const settings = ref<CheckinSettings>({ enabled: false, remindAt: null })
  const loading = ref(false)
  const saving = ref(false)

  const enabled = computed(() => settings.value.enabled)
  const doneToday = computed(() => today.value !== null)

  async function loadSettings() {
    settings.value = await api.get<CheckinSettings>('/checkin-settings')
    return settings.value
  }

  async function load(date = todayString()) {
    loading.value = true
    try {
      const [catalogue, mine] = await Promise.all([
        words.value.body.length > 0
          ? Promise.resolve(words.value)
          : api.get<CheckinWords>('/checkin/words'),
        api.get<Checkin | null>(`/checkin/${date}`),
      ])
      words.value = catalogue
      today.value = mine
    } finally {
      loading.value = false
    }
  }

  async function save(rating: number, wordIds: string[], date = todayString()) {
    saving.value = true
    try {
      today.value = await api.put<Checkin>('/checkin', { date, rating, wordIds })
      // Answered, so there is nothing to be reminded about until tomorrow.
      await scheduleCheckinReminder(settings.value, { answeredToday: true })
      return today.value
    } finally {
      saving.value = false
    }
  }

  async function clear(date = todayString()) {
    await api.delete(`/checkin/${date}`)
    today.value = null
  }

  async function saveSettings(next: CheckinSettings) {
    settings.value = await api.put<CheckinSettings>('/checkin-settings', next)
    await scheduleCheckinReminder(settings.value, { answeredToday: doneToday.value })
    return settings.value
  }

  /**
   * Put the reminder back as the settings say (M-112).
   *
   * Android drops an app's alarms when the app is updated or force-stopped,
   * and the notification plugin restores them only after a reboot. Scheduled
   * only when the settings changed, the reminder silently vanished with the
   * next update. So the app puts it back whenever it is opened or comes to
   * the front: cancelling and scheduling the one fixed notification, never
   * asking for permission on the way.
   */
  let ensuredAt = 0
  async function ensureReminder() {
    if (!remindersAvailable() || Date.now() - ensuredAt < ENSURE_GAP_MS) return
    try {
      await loadSettings()
    } catch {
      // No signal or no session: the reminder already set, if any, stands.
      return
    }
    ensuredAt = Date.now()
    await scheduleCheckinReminder(settings.value, { answeredToday: doneToday.value, ask: false }).catch(() => undefined)
  }

  /** A range, for the statistics page. Still no arithmetic over it. */
  async function between(from: string, to: string) {
    return api.get<Checkin[]>('/checkin', { from, to })
  }

  return {
    words,
    today,
    settings,
    loading,
    saving,
    enabled,
    doneToday,
    load,
    loadSettings,
    save,
    clear,
    saveSettings,
    ensureReminder,
    between,
  }
})
